import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Phone, Heart, AlertCircle } from 'lucide-react';

/**
 * Red Flag Alert Component
 * Displays calm, non-panic safety guidance for high-risk symptoms
 */
const RedFlagAlert = ({ redFlag }) => {
  if (!redFlag || !redFlag.triggered) {
    return null;
  }

  const handleCallNHS111 = () => {
    window.location.href = 'tel:111';
  };

  const handleCall999 = () => {
    window.location.href = 'tel:999';
  };

  return (
    <Card className="border-2 border-red-300 bg-red-50 shadow-lg animate-in fade-in duration-300">
      <CardHeader className="pb-4">
        <div className="flex items-start gap-4">
          <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-red-900 text-xl mb-2 flex items-center gap-2">
              This may need urgent attention
            </CardTitle>
            <p className="text-red-800 text-base font-normal">
              Based on what you've shared, it's important to seek medical advice promptly.
            </p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Medical Context */}
        <div className="bg-white rounded-lg p-4 border border-red-200">
          <p className="text-gray-800 text-sm leading-relaxed">
            {redFlag.message}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-red-900 mb-2">What to do now:</p>
          
          {/* NHS 111 - Primary recommendation */}
          <Button
            onClick={handleCallNHS111}
            className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold shadow-md flex items-center justify-center gap-3"
          >
            <Phone className="h-5 w-5" />
            <span>Call NHS 111 for guidance</span>
          </Button>

          {/* Contact GP */}
          <div className="p-4 bg-white rounded-lg border-2 border-blue-200">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900 text-sm">Contact your GP surgery</p>
                <p className="text-xs text-gray-600 mt-1">
                  Ask for an urgent appointment or speak to the duty doctor
                </p>
              </div>
            </div>
          </div>

          {/* 999 Emergency - Only for severe cases */}
          <div className="p-4 bg-red-100 rounded-lg border-2 border-red-300">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-900 text-sm mb-1">Call 999 if:</p>
                <ul className="text-xs text-red-800 space-y-1">
                  <li>• Symptoms suddenly get worse</li>
                  <li>• You have severe chest pain or can't breathe</li>
                  <li>• Someone is unresponsive or having a seizure</li>
                </ul>
                <Button
                  onClick={handleCall999}
                  variant="destructive"
                  className="mt-3 w-full bg-red-600 hover:bg-red-700"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call 999 Emergency
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Reassurance */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
          <div className="flex items-start gap-2">
            <Heart className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-green-900 font-medium">
                You're not alone — getting help early is the right step.
              </p>
              <p className="text-xs text-green-800 mt-1">
                Healthcare professionals are here to help. It's always better to seek advice when you're concerned.
              </p>
            </div>
          </div>
        </div>

        {/* Evidence-based Disclaimer */}
        <div className="text-xs text-gray-600 pt-2 border-t border-red-200">
          <p>
            <strong>Note:</strong> This alert is based on recognized clinical red flags from NHS and NICE guidelines. 
            It does not replace professional medical assessment.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default RedFlagAlert;
